import { activeStatuses, definitionNotHeld, ec, ecId, HandBack, handBack, insertRoll, itemField, record, refuse, upper,
  type JanusCtx, type JanusReaders, type Row } from './context.js';
import type { JanusInput } from './janus.js';
import { janusShapeshiftFormAllowed, janusTargetImmuneToShapeshift } from '../port/k3-rules/apply_shapeshift_post_effect.js';
import { calcVRandomFromVDice } from '../port/k3-rules/ac_and_vitality_calculators.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import { empty, equal } from '../php-compat/index.js';
import { carrierHasTrigger, layerListEntryUuid } from './statuses/status-trigger-gate.js';
import { checkOpposingTeam } from './check-opposing-team.js';
import { applyStatusEffect, skipImmuneStatus } from './statuses/apply-status-effect.js';
import { getStatusEffectDurationTimes } from './statuses/get-status-effect-duration-times.js';
import { statusItemId } from '../statuses/expiry.js';
import { plCatalogs } from '../pl/contract.js';
import { absorbShapeshiftInventory, shapeshiftInventory } from '../statuses/shapeshift-inventory.js';
import { ancestralBodyHeightFt, encounterIsShapeless, formBodyHeightFt, legacyBodyHeightFt, legacyRegroundAfterSizeChange } from '../statuses/legacy-reground.js';
import { lostShapeshift } from '../port/k4-dispatch/status_removal_api.js';
import { appendFormInventoryWrites } from '../statuses/shapeshift-transition.js';
import { creatureName } from '../statuses/gm-boundary.js';
import { resolveRemovalSupport } from '../statuses/removal-support.js';
import { recompileMemberStatusDiff } from '../compile/member-status-diff.js';
import { applyMemberStatusRoots, type MemberStatusStep, type MemberRecompileResult } from '../compile/member-status-recompile.js';
import { planStoredStatusRemoval } from './statuses/status-effect-removal-helpers.js';
import { frameCopyOnRead } from './frame-snapshot.js';

export const SHAPESHIFT_LAYERS = ['shapeshift', 'user_choice_1_shapeshift', 'user_choice_2_shapeshift',
  'user_choice_3_shapeshift', 'user_choice_4_shapeshift'] as const;
const UUID = /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/;
type Readers = Pick<JanusReaders, 'value' | 'entries' | 'definition'>;

/** Removal helpers may collect restores; only the initiating commit can arm new absorption. */
export function canCommitShapeshift(ctx: JanusCtx): boolean {
  return ctx.shapeshiftInventory !== undefined && !!ctx.shapeshiftActorId;
}

function shapeshiftEnvironment(read: Readers) {
  return browserDependencies({
    layer_value: (row: Row, key: string) => read.value(row, key),
    layer_entries: (row: Row, key: string) => read.entries(row, key),
    layer_list_entry_uuid: layerListEntryUuid,
    passive_ability_entry_is_active: (entry: unknown) => {
      const row = record(entry);
      return !row || !('passive_conditions_active' in row) || !empty(read.value(row, 'passive_conditions_active'));
    },
    check_uuid: (value: unknown) => UUID.test(upper(value)),
    query_full_item_arr: (id: string) => read.definition(upper(id)) ?? definitionNotHeld('shapeshift:definition-not-held', id),
  });
}

/** Page and engine share the PHP-authored pool check, including wrapped item identities. */
export function isShapeshiftPickVariable(ctx: { read: Readers }, ability: Row, v1: unknown, v2: unknown): boolean {
  return v2 == null && typeof v1 === 'string' && UUID.test(upper(v1))
    && janusShapeshiftFormAllowed(ability, upper(v1), shapeshiftEnvironment(ctx.read));
}

/** apply_shapeshift_post_effect.php, after summon and before push/pull. All writes belong to this use's verdict. */
export function applyShapeshiftPostEffect(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row): void {
  if (!SHAPESHIFT_LAYERS.some(key => ctx.read.has(ability, key))) return;
  const replacing = activeStatuses(ctx, target).some(status => itemField(ctx.read.definition(statusItemId(status))
    ?? record(status.status_arr), 'item_type') === 'creature');
  try { applyShapeshift(ctx, input, ability, caster, target); }
  catch (error) {
    // Missing definitions retain their identity so the lane can hydrate them before retrying the verdict.
    if (!(error instanceof HandBack) || !replacing || /definition-not-held/.test(error.reason)) throw error;
    refuse(`${String(itemField(ability, 'item_name') ?? 'Shapeshift')} cannot transform ${creatureName(ctx, ecId(target))} with the form data currently available.`);
  }
}

function applyShapeshift(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row): void {
  if (!canCommitShapeshift(ctx)) handBack('post_effect_conditions:shapeshift');
  if (!ecId(target)) handBack('shapeshift:non-creature-target');
  const pick = upper(input.useAbilityVariable1);
  const formId = UUID.test(pick) ? pick : layerListEntryUuid(ctx.read.value(ability, 'shapeshift'));
  if (!UUID.test(formId)) handBack('shapeshift:form-not-chosen');
  if (UUID.test(pick) && !isShapeshiftPickVariable(ctx, ability, input.useAbilityVariable1, input.useAbilityVariable2))
    refuse('This form is not offered by this ability or spell!');
  if (checkOpposingTeam(ctx, caster, target) && janusTargetImmuneToShapeshift(target, shapeshiftEnvironment(ctx.read))) {
    insertRoll(ctx, { roll_type_1: 'target_immune_to_status_effect', roll_item_id: formId, by: caster, to: target });
    return;
  }
  const form = ctx.read.definition(formId);
  if (!form || !record(form.item_layer_types)) definitionNotHeld('shapeshift:definition-not-held', formId);
  if (skipImmuneStatus(ctx, formId, form, caster, target)) return;
  // Measure the actual old form once: intermediate reversion and the new form must preserve the same feet.
  const forms = activeStatuses(ctx, target).filter(status => {
    const id = statusItemId(status);
    const definition = ctx.read.definition(id) ?? record(status.status_arr);
    if (!definition) definitionNotHeld('shapeshift:status-definition-not-held', id);
    return itemField(definition, 'item_type') === 'creature';
  });
  if (forms.length > 1) handBack('shapeshift:multiple-held-forms');
  const inventoryRemains = ctx.read.has(ability, 'shapeshift_inventory_remains_on_form')
    && !(ctx.read.has(ability, 'user_choice_1_shapeshift_inventory_remains_on_form') && input.useAbilityVariable1 != null
      && equal(input.useAbilityVariable1, 0));
  const shapeless = encounterIsShapeless(ctx.encounter);
  const ancestral = shapeless ? forms.length ? ancestralBodyHeightFt(target, new Set(forms.map(statusItemId))) : legacyBodyHeightFt(target) : null;
  if (shapeless && ancestral === null) handBack('lost_shapeshift:ancestral-size-not-held');
  const heights = shapeless
    ? [ancestral!, formBodyHeightFt(target, formId, form, upper(layerListEntryUuid(ctx.read.value(form, 'size'))) || null)] as const : null;
  let original: Extract<MemberRecompileResult, { ok: true }> | null = null;
  let replacement: Extract<MemberRecompileResult, { ok: true }> | null = null;
  if (forms.length) {
    const returning = shapeshiftInventory(ctx, target);
    if (returning.some(write => write.kind === 'unequip_acquired') || inventoryRemains && returning.length
      || !inventoryRemains && absorbShapeshiftInventory(ctx, target).length)
      handBack('shapeshift:chain-inventory-projection-unavailable');
    const plan = planStoredStatusRemoval(ctx, target, forms[0]!, 'form');
    if (plan.lossArm) handBack('shapeshift:chain-loss-listener-not-ported');
    if (plan.peers.some(peer => statusItemId(peer.status) !== plCatalogs().statusEffects.getId('spell_locked')))
      handBack('shapeshift:chain-peer-effects-not-ported');
    const remove: MemberStatusStep[] = [forms[0]!, ...plan.peers.map(peer => peer.status)].map(status => ({ kind: 'remove',
      statusItemId: statusItemId(status), statusId: upper(ctx.read.value(status, 'status_id')) }));
    const scene = { definition: ctx.read.definition, catalog: record(target.catalog), roster: () => [...ctx.members.values()] };
    const source = ctx.originals.get(ecId(target)) ?? target;
    const projections = ctx.shapeshiftProjections?.get(source) ?? new Map<string, MemberRecompileResult | null>();
    ctx.shapeshiftProjections?.set(source, projections);
    const project = (steps: MemberStatusStep[]): MemberRecompileResult | null => {
      const key = JSON.stringify(steps);
      if (!projections.has(key)) projections.set(key, recompileMemberStatusDiff(target, steps, scene));
      const result = projections.get(key)!;
      // Stages own writes to projected clusters; keep the cached result untouched.
      // Only the projection is viewed here, never the held member or encounter.
      return result?.ok ? frameCopyOnRead(result, new Map(), { catalogs: 'share' }).encounter as MemberRecompileResult : result;
    };
    const before = project(remove);
    const after = project([...remove, { kind: 'add', statusItemId: formId, casterId: ecId(caster) }]);
    // Only the affected-cluster projector can certify a replacement. Never fall through
    // to a whole-member compile or install B while A's defenses/geometry remain held.
    if (!before?.ok || !after?.ok) handBack('shapeshift:form-transition-projection-unavailable');
    original = before; replacement = after;
  }
  const install = (owner: Row, result: Extract<MemberRecompileResult, { ok: true }>): void => {
    const computed = record(owner.computed) ?? (owner.computed = {});
    computed.creature = result.creature; computed.vitality = result.vitality;
    applyMemberStatusRoots(owner, result);
  };
  if (forms.length) lostShapeshift(ctx, target, 'lost_shapeshift', false, { deferSupport: true,
    beforeLoss: (_ctx, owner) => { if (original) install(owner, original); } });
  const ends = getStatusEffectDurationTimes(ctx, ability, caster);
  ctx.statusConnectedId ??= ctx.read.uuid();
  if (!empty(ctx.read.value(ability, 'shapeshift_monster_template'))) handBack('shapeshift:monster-template');
  if (forms.length) {
    const gains = [formId, ...(ctx.read.has(ability, 'spell_locked') ? [plCatalogs().statusEffects.getId('spell_locked') ?? ''] : [])];
    if (carrierHasTrigger(ctx, target, 'triggers_when_caster_gains_status_effect', 'shapeshift:gain-listener', gains)
      || carrierHasTrigger(ctx, caster, 'triggers_when_target_gains_status_effect', 'shapeshift:gain-listener', gains))
      handBack('shapeshift:chain-gain-listener-not-ported');
  }
  const inventory = inventoryRemains ? [] : absorbShapeshiftInventory(ctx, target);
  applyStatusEffect(ctx, input, ability, formId, 'shapeshifted', caster, target, ends, false, false, 0, false, null);
  if (!empty(ctx.read.value(ability, 'spell_locked'))) {
    const lockId = plCatalogs().statusEffects.getId('spell_locked');
    if (!lockId) handBack('shapeshift:spell-lock-not-held');
    applyStatusEffect(ctx, input, ability, lockId, 'shapeshifted', caster, target, ends, false, false, 0, true, null);
  }
  appendFormInventoryWrites(ctx, inventory);
  const dice = Math.trunc(Number(ctx.read.value(form, 'v_dice') ?? 0)), body = Number(ctx.read.value(form, 'body') ?? 0);
  if (!Number.isSafeInteger(dice) || dice < 0 || !Number.isFinite(body)) handBack('shapeshift:vitality-not-held');
  const vTemp = Math.max(0, calcVRandomFromVDice(layerListEntryUuid(ctx.read.value(form, 'size')), dice, body, 0,
    browserDependencies({ roll_dice: (count: number, die: number, mod: number) => {
      let v = mod;
      for (let i = 0; i < count; i++) v += ctx.roll.d(die);
      return v;
    } })));
  ec(target).v_temp = vTemp;
  if (replacement) install(target, { ...replacement, vitality: { ...replacement.vitality, base_v_temp: vTemp, v_temp: vTemp } });
  if (heights) legacyRegroundAfterSizeChange(ctx, target, heights[0], heights[1]);
  if (forms.length) {
    // Support is judged once against B, including its pending form row. The temporary
    // original body between removal and application must never fall or take damage.
    const bucket = record(target.statuses) ?? (target.statuses = {}), held = bucket.target;
    bucket.target = activeStatuses(ctx, target);
    try { resolveRemovalSupport(ctx, ecId(target), forms[0]!); }
    finally { if (held === undefined) delete bucket.target; else bucket.target = held; }
  }
  insertRoll(ctx, { roll_type_1: 'shapeshifted', roll_item_id: formId, by: caster, to: target });
}
